import * as React from "react"
import { useMutation } from "@tanstack/react-query"
import { api } from "@/api/instance"
import type { IntentOut } from "@/api/client"
import { decideLink } from "./decide"

export type PartnerLaunchData = {
  phone: string
  session_id: string
  profile_name: string
  intent_id?: string | null
}

export type UseLaunchLinkOptions = {
  readonly ready: boolean
  readonly isRecordingRef: React.RefObject<boolean>
  readonly adopt: (intentId: string) => Promise<IntentOut | null>
  readonly beginLaunch: (target: {
    phone: string
    sessionId: string | null
  }) => void
  readonly onError: (message: string) => void
  readonly onConsumed: () => void
}

function launchLinkErrorMessage(err: unknown): string {
  const status = (
    err as { response?: { status?: number; data?: { type?: string } } }
  )?.response?.status
  const problemType =
    (err as { response?: { data?: { type?: string } } })?.response?.data?.type ??
    ""
  if (status && status >= 400 && status < 500) {
    return problemType.includes("DesignerMismatch")
      ? "Ссылка на встречу выдана для другого дизайнера. Войдите под пользователем, для которого создана встреча, и откройте ссылку заново."
      : "Ссылка на встречу устарела или недействительна. Создайте встречу заново и откройте свежую ссылку."
  }
  return "Не удалось открыть встречу по ссылке: проблема с соединением. Обновите страницу, чтобы попробовать снова."
}

/**
 * Resolves a stored partner launch token once the page is ready:
 * GET /v1/partner/launch → optional adopt → beginLaunch.
 */
export function useLaunchLink(options: UseLaunchLinkOptions): void {
  const {
    ready,
    isRecordingRef,
    adopt,
    beginLaunch,
    onError,
    onConsumed,
  } = options
  const handledRef = React.useRef(false)
  const adoptRef = React.useRef(adopt)
  const beginLaunchRef = React.useRef(beginLaunch)
  const onErrorRef = React.useRef(onError)
  const onConsumedRef = React.useRef(onConsumed)
  const isRecordingRefStable = isRecordingRef

  React.useEffect(() => {
    adoptRef.current = adopt
    beginLaunchRef.current = beginLaunch
    onErrorRef.current = onError
    onConsumedRef.current = onConsumed
  })

  const launchMutation = useMutation({
    mutationFn: async (token: string) => {
      const res = await api.instance.get<PartnerLaunchData>(
        `/v1/partner/launch`,
        { params: { token } },
      )
      return res.data
    },
    onError: (err) => {
      sessionStorage.removeItem("launch_token")
      onErrorRef.current(launchLinkErrorMessage(err))
    },
  })

  const mutateLaunch = launchMutation.mutate

  React.useEffect(() => {
    if (handledRef.current || !ready) return
    const token = sessionStorage.getItem("launch_token")
    if (!token) return
    handledRef.current = true

    mutateLaunch(token, {
      onSuccess: async (data) => {
        sessionStorage.removeItem("launch_token")
        onConsumedRef.current()
        const adopted = data.intent_id
          ? await adoptRef.current(data.intent_id)
          : null
        if (decideLink(adopted, isRecordingRefStable.current) === "begin") {
          beginLaunchRef.current({
            phone: data.phone,
            sessionId: data.session_id,
          })
        }
      },
    })
  }, [ready, isRecordingRefStable, mutateLaunch])
}
